import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVideoCall } from '../hooks/useVideoCall';
import { useAuth } from '../hooks/useAuth';
import { CallType } from '../services/videoCallService';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  Monitor,
  MonitorOff,
  Maximize2,
  Minimize2,
  Users,
  Settings,
  Volume2,
  VolumeX
} from 'lucide-react';

interface VideoCallPageProps {
  callId?: number;
  conversationId?: number;
  isIncoming?: boolean;
}

const VideoCallPage: React.FC<VideoCallPageProps> = ({
  callId: initialCallId,
  conversationId: initialConversationId,
  isIncoming = false
}) => {
  const { callId: paramCallId, conversationId: paramConversationId } = useParams<{
    callId?: string;
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const callId = initialCallId || (paramCallId ? parseInt(paramCallId) : undefined);
  const conversationId = initialConversationId || (paramConversationId ? parseInt(paramConversationId) : undefined);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedRemoteStream, setSelectedRemoteStream] = useState<string | null>(null);

  const {
    call,
    localStream,
    remoteStreams,
    isAudioEnabled,
    isVideoEnabled,
    isSharingScreen,
    callDuration,
    isConnected,
    initiateCall,
    answerCall,
    endCall,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    formatDuration
  } = useVideoCall({
    callId,
    conversationId,
    userId: user?.id.toString() || '',
    onCallEnd: () => {
      navigate('/messages');
    },
    onError: (err) => {
      console.error('Call error:', err);
    }
  });

  // Initialize call
  useEffect(() => {
    if (isIncoming && callId) {
      // Answer incoming call
      answerCall(callId, CallType.VIDEO);
    } else if (conversationId && !call) {
      // Initiate new call
      initiateCall(CallType.VIDEO);
    }
  }, [isIncoming, callId, conversationId, call]);

  // Attach local stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Attach remote streams
  useEffect(() => {
    remoteStreams.forEach((stream, peerId) => {
      const videoElement = remoteVideoRefs.current.get(peerId);
      if (videoElement) {
        videoElement.srcObject = stream;
      }
    });

    // Auto-select first remote stream
    if (!selectedRemoteStream && remoteStreams.size > 0) {
      setSelectedRemoteStream(Array.from(remoteStreams.keys())[0]);
    }
  }, [remoteStreams, selectedRemoteStream]);

  // Handle end call
  const handleEndCall = async () => {
    await endCall();
  };

  // Handle toggle audio
  const handleToggleAudio = () => {
    const newState = toggleAudio();
    setIsMuted(!newState);
  };

  // Handle toggle video
  const handleToggleVideo = () => {
    toggleVideo();
  };

  // Handle screen share
  const handleScreenShare = async () => {
    if (isSharingScreen) {
      await stopScreenShare();
    } else {
      try {
        await startScreenShare();
      } catch (error) {
        console.error('Failed to start screen share:', error);
      }
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const mainStream = selectedRemoteStream ? remoteStreams.get(selectedRemoteStream) : null;
  const otherStreams = Array.from(remoteStreams.entries()).filter(([id]) => id !== selectedRemoteStream);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 z-50">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-black/30 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {call?.caller && (
              <>
                <img
                  src={call.caller.avatarUrl || '/default-avatar.png'}
                  alt={call.caller.displayName}
                  className="w-10 h-10 rounded-full ring-2 ring-blue-500"
                />
                <div>
                  <h2 className="text-white font-semibold">
                    {call.caller.displayName}
                  </h2>
                  <p className="text-gray-300 text-sm">
                    {isConnected ? formatDuration(callDuration) : 'Đang kết nối...'}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 bg-white/10 rounded-lg text-white text-sm flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>{remoteStreams.size + 1}</span>
            </div>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>

            <button
              onClick={toggleFullscreen}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5" />
              ) : (
                <Maximize2 className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Video Grid */}
        <div className="flex-1 relative p-4">
          {/* Main Video (Remote or Screen Share) */}
          <div className="h-full relative bg-black rounded-xl overflow-hidden">
            {mainStream ? (
              <video
                ref={(el) => {
                  if (el && selectedRemoteStream) {
                    remoteVideoRefs.current.set(selectedRemoteStream, el);
                    el.srcObject = mainStream;
                  }
                }}
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                  <Video className="w-16 h-16 text-white" />
                </div>
                <p className="text-white text-lg">Đang chờ người khác tham gia...</p>
              </div>
            )}

            {/* Connection Status */}
            {!isConnected && (
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                Đang kết nối...
              </div>
            )}
          </div>

          {/* Local Video (Picture-in-Picture) */}
          <div className="absolute bottom-6 right-6 w-64 h-48 bg-black rounded-xl overflow-hidden shadow-2xl ring-2 ring-blue-500">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover mirror"
            />
            {!isVideoEnabled && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {user?.displayName?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
            )}
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 backdrop-blur text-white text-xs rounded">
              Bạn
            </div>
            {!isAudioEnabled && (
              <div className="absolute top-2 left-2 p-1.5 bg-red-600 rounded-full">
                <MicOff className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Other Participants (if group call) */}
          {otherStreams.length > 0 && (
            <div className="absolute top-6 right-6 space-y-3">
              {otherStreams.map(([peerId, stream]) => (
                <div
                  key={peerId}
                  onClick={() => setSelectedRemoteStream(peerId)}
                  className="w-48 h-36 bg-black rounded-lg overflow-hidden shadow-xl ring-2 ring-white/30 cursor-pointer hover:ring-blue-500 transition-all"
                >
                  <video
                    ref={(el) => {
                      if (el) {
                        remoteVideoRefs.current.set(peerId, el);
                        el.srcObject = stream;
                      }
                    }}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-black/30 backdrop-blur-sm px-6 py-6">
          <div className="flex items-center justify-center gap-4">
            {/* Toggle Audio */}
            <button
              onClick={handleToggleAudio}
              className={`p-4 rounded-full transition-all ${
                isAudioEnabled
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {isAudioEnabled ? (
                <Mic className="w-6 h-6 text-white" />
              ) : (
                <MicOff className="w-6 h-6 text-white" />
              )}
            </button>

            {/* Toggle Video */}
            <button
              onClick={handleToggleVideo}
              className={`p-4 rounded-full transition-all ${
                isVideoEnabled
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {isVideoEnabled ? (
                <Video className="w-6 h-6 text-white" />
              ) : (
                <VideoOff className="w-6 h-6 text-white" />
              )}
            </button>

            {/* Screen Share */}
            <button
              onClick={handleScreenShare}
              className={`p-4 rounded-full transition-all ${
                isSharingScreen
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              {isSharingScreen ? (
                <MonitorOff className="w-6 h-6 text-white" />
              ) : (
                <Monitor className="w-6 h-6 text-white" />
              )}
            </button>

            {/* End Call */}
            <button
              onClick={handleEndCall}
              className="p-4 rounded-full bg-red-600 hover:bg-red-700 transition-all"
            >
              <PhoneOff className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Call Duration */}
          <div className="text-center mt-4">
            <p className="text-white text-sm opacity-70">
              Thời gian: {formatDuration(callDuration)}
            </p>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Cài đặt cuộc gọi</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <span className="text-white text-xl">×</span>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white font-medium mb-2">
                  Âm thanh
                </label>
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-gray-400" />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    defaultValue="70"
                    className="flex-1"
                  />
                  <span className="text-white text-sm">70%</span>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-700">
                <h4 className="text-white font-medium mb-3">Thông tin cuộc gọi</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Trạng thái:</span>
                    <span className="text-white">
                      {isConnected ? 'Đã kết nối' : 'Đang kết nối'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Số người tham gia:</span>
                    <span className="text-white">{remoteStreams.size + 1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Thời gian:</span>
                    <span className="text-white">{formatDuration(callDuration)}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowSettings(false)}
              className="w-full mt-6 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallPage;
